import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => {
      el.classList.remove('opacity-0', 'translate-y-6');
      el.classList.add('opacity-100', 'translate-y-0');
    }, 100);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/1bd71f92a319f977a225ccb75d924cef.jpg"
          alt="Sweet Celebrations beautiful custom cake"
          className="w-full h-full object-cover object-top"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/80 via-charcoal-900/50 to-charcoal-900/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/40 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-20">
        <div
          ref={heroRef}
          className="max-w-2xl transition-all duration-1000 ease-out opacity-0 translate-y-6"
        >
          {/* Location badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="h-px w-8 bg-gold-400" />
            <span className="text-gold-400 text-xs uppercase tracking-[0.3em] font-sans font-semibold">
              Lubbock, Texas
            </span>
            <div className="h-px w-8 bg-gold-400" />
          </div>

          {/* Headline */}
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
            Custom Sweet Treats<br />
            <em className="italic text-blush-300">for Every</em><br />
            Celebration
          </h1>

          {/* Subheadline */}
          <p className="text-cream-200 text-base md:text-lg leading-relaxed mb-10 max-w-xl font-sans font-light">
            Sweet Celebrations in Lubbock, Texas creates custom cakes, cookies, pastries, pies, and more — beautifully made to match your vision.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/custom-orders"
              className="inline-flex items-center justify-center gap-2 bg-blush-500 hover:bg-blush-600 text-white px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 hover:scale-105 cursor-pointer"
            >
              Request a Custom Order
              <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="#gallery"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white hover:bg-white/10 px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 cursor-pointer"
            >
              View Our Treats
              <i className="ri-arrow-down-line" />
            </a>
          </div>

          {/* Stats row */}
          <div className="mt-16 flex flex-wrap gap-8">
            {[
              { value: '17+', label: 'Years of Experience' },
              { value: '100%', label: 'Custom Creations' },
              { value: 'Lubbock', label: 'Locally Loved' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="font-serif text-3xl text-gold-400">{stat.value}</span>
                <span className="text-cream-300 text-xs font-sans uppercase tracking-widest">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <i className="ri-arrow-down-s-line text-white/60 text-2xl" />
      </div>
    </section>
  );
}
